import { createEffect, createMemo, onCleanup, onMount } from "solid-js";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

import { defaultChannelsStore } from "../../../store/defaultChannelsStore";
import { getCurrentDatasetTimezone } from "../../../store/datasetTimezoneStore";
import { liveConfigStore } from "../../../store/liveConfigStore";
import { getDisplayWindowReferenceTime, isPlaying, liveFollow, seekLive, selectedTime, setIsManualTimeChange, setIsPlaying, setSelectedTime, smoothPlaybackTimeForTrack, timeWindow, stopPlaybackClock } from "../../../store/playbackStore";
import { sourcesStore } from "../../../store/sourcesStore";
import { streamingStore } from "../../../store/streamingStore";
import { liveColumnarStore } from "../../../store/liveColumnarStore";
import { themeStore } from "../../../store/themeStore";
import { config } from "@config/env";
import { debug as logDebug, warn as logWarn } from "../../../utils/console";
import { formatTime } from "../../../utils/global";
import { liveReplayFilteredDataOptions } from "../../../utils/liveReplayFilteredData";
import { resolveLiveWindowAnchorMs } from "../../../utils/liveTimelineWindowAnchor";
import { createStallBackoff, LIVE_POLL_BASE_MS, LIVE_POLL_MAX_MS } from "../../../utils/liveStallBackoff";
import { isRenderYieldActive } from "../../../utils/renderYield";

interface LiveMapUPlotTimeSeriesProps {
  samplingFrequency?: number;
  onStableSelectedTimeChange?: (time: Date) => void;
  onMapUpdate?: (data: LiveDataPoint[]) => void;
  selectedSourceIds?: Set<number>;
  /** When false, onMapUpdate runs only for the initial history load; live map layers read streamingStore directly. */
  incrementalMapUpdates?: boolean;
  /** Live replay timeline. Scrubbable unless config.LIVE_MAP_SCRUB is off. */
  replayMode?: boolean;
  [key: string]: unknown;
}

type LiveDataPoint = Record<string, unknown>;

/**
 * `warn` ships to the server over HTTP regardless of VITE_VERBOSE, so a warning inside the uPlot
 * redraw path posts once per frame while the failure persists. Rate-limit per message.
 */
const WARN_COOLDOWN_MS = 60_000;
const lastWarnAtByKey = new Map<string, number>();
const warnThrottled = (key: string, ...args: unknown[]): void => {
  const now = Date.now();
  const last = lastWarnAtByKey.get(key);
  if (last != null && now - last < WARN_COOLDOWN_MS) return;
  lastWarnAtByKey.set(key, now);
  logWarn(...(args as [string, ...unknown[]]));
};

type SourceGroup = {
  sourceId: number;
  sourceName: string;
  color: string;
  data: LiveDataPoint[];
};

type SeriesMeta = {
  sourceId: number;
  label: string;
  color: string;
};

type ChartModel = {
  data: uPlot.AlignedData;
  seriesMeta: SeriesMeta[];
  xRange: [number, number];
  yRange: [number, number];
};

const LINE_GAP_THRESHOLD_MS = 30_000;
const DEFAULT_WINDOW_MS = 30 * 60 * 1000;
const END_THRESHOLD_MS = 1_000;
/** uPlot padding [top, right, bottom, left] — left 0 shifts plot 50px left vs prior D3/uPlot gutter. */
const CHART_PADDING: [number, number, number, number] = [10, 10, 30, 0];
const Y_AXIS_GUTTER_PX = 50;
const X_TICK_SIZE_PX = 5;

const FALLBACK_Y_RANGE: [number, number] = [0, 100];

/** Is the timeline allowed to drive the playhead at all? */
const scrubEnabled = (): boolean => config.LIVE_MAP_SCRUB !== false;

function formatYAxisTick(value: number): string {
  if (!Number.isFinite(value)) return "";
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** Always expose at least min/max on the y-axis (compact live map timeline). */
function yAxisSplits(min: number, max: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 100];
  if (min === max) {
    const pad = Math.max(1, Math.abs(min) * 0.1);
    return [min - pad, min + pad];
  }
  return [min, max];
}

function readFiniteNumber(value: unknown): number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function getTimestamp(d: LiveDataPoint | undefined): Date {
  if (!d) return new Date(0);
  const timestamp = d.Datetime ?? d.timestamp ?? d.time ?? d.datetime;
  if (!timestamp) return new Date(0);
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === "number") return new Date(timestamp);
  if (typeof timestamp === "string") {
    const parsed = new Date(timestamp);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return new Date(0);
}

function getBspValue(d: LiveDataPoint, bspFieldName: string): number | undefined {
  const value = readFiniteNumber(d[bspFieldName] ?? d.Bsp_kph ?? d.Bsp_kts ?? d.Bsp ?? d.bsp);
  // Live packet loss can be encoded as 0; ignore it rather than drawing bottom spikes.
  return value !== undefined && value > 0 ? value : undefined;
}

function sourceSetKey(sourceIds: Set<number>): string {
  return [...sourceIds].sort((a, b) => a - b).join(",");
}

function sameSourceSet(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

function validDate(value: unknown): value is Date {
  return value instanceof Date && Number.isFinite(value.getTime());
}

export default function LiveMapUPlotTimeSeries(props: LiveMapUPlotTimeSeriesProps) {
  let chartContainer: HTMLDivElement | null = null;
  /** Detaches the current plot's scrub listeners; re-set every time uPlot is recreated. */
  let scrubTeardown: (() => void) | null = null;
  /**
   * End of the DISPLAYED window while paused, frozen at the moment following stopped.
   * Null whenever the timeline is following the live edge. See `windowAnchorTime`.
   */
  let pausedWindowAnchorMs: number | null = null;
  let plot: uPlot | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let renderRafId: number | null = null;
  let lastSeriesSignature = "";
  let lastMapUpdateSignature: string | null = null;
  let lastSeenStreamLifecycle = -1;
  let prevIsPlayingForMap = false;
  let hasInitializedSelectedTime = false;
  let lastLoadedSourceIds = new Set<number>();
  let lastTimeWindow = Number(timeWindow());
  let isFetchingData = false;
  let isMounted = true;
  let abortController: AbortController | null = null;
  let currentModel: ChartModel | null = null;
  // End-of-data catch-up guards. This effect re-runs on every selectedTime() publish (~1/s in live
  // mode), and each run is a channels x sources `/data/merged` wave, so without an in-flight latch
  // and a min-interval it hammers a feed that is by definition not producing. Kept local rather than
  // shared with the playback-window pull: that one does different work on a different cadence.
  let endOfDataFetchInFlight = false;
  let lastEndOfDataFetchMs = 0;
  const endOfDataBackoff = createStallBackoff({ baseMs: LIVE_POLL_BASE_MS, maxMs: LIVE_POLL_MAX_MS });

  const selectedSourceIds = (): Set<number> => props.selectedSourceIds ?? new Set<number>();
  const isReplayMode = (): boolean => props.replayMode === true;
  const getTimezone = () => getCurrentDatasetTimezone();

  /**
   * End of the DISPLAYED window — deliberately not the same thing as the playhead.
   *
   * ONE memo, read by both `timeExtent` (which sets the x scale) and `groupedData` (which selects
   * the rows). They have to agree: the x range is computed straight from a window end, so anchoring
   * only the data left the scale still riding the playhead and the frame still moved on every click.
   *
   * The rule itself is `resolveLiveWindowAnchorMs` — pure and unit-tested; this holds the latch.
   */
  const windowAnchorMs = createMemo<number>(() => {
    const requested = Number(timeWindow());
    const windowMinutes = requested > 0 ? requested : 5;
    const reference = getDisplayWindowReferenceTime();
    const playhead = validDate(reference) ? reference : selectedTime();
    const playheadMs = validDate(playhead) ? playhead.getTime() : Date.now();

    pausedWindowAnchorMs = resolveLiveWindowAnchorMs({
      following: liveFollow(),
      playheadMs,
      windowMinutes,
      currentAnchorMs: pausedWindowAnchorMs,
    });

    return pausedWindowAnchorMs ?? playheadMs;
  });

  const groupedData = createMemo<SourceGroup[]>(() => {
    const selected = selectedSourceIds();
    const currentTimeWindow = Number(timeWindow());
    const windowMin = currentTimeWindow > 0 ? currentTimeWindow : 5;
    // The WINDOW hangs off this; the playhead marker is drawn separately from selectedTime.
    const referenceTime = new Date(windowAnchorMs());
    const playhead = validDate(referenceTime) ? referenceTime : selectedTime();
    const currentlyPlaying = isPlaying();

    if (selected.size === 0) return [];

    // GPU/columnar path: build groups from the columnar store (same shape), bypassing streamingStore.
    if (config.LIVE_MAP_GPU) {
      void liveColumnarStore.epoch();
      if (currentlyPlaying) void smoothPlaybackTimeForTrack();
      const endMs = validDate(playhead)
        ? playhead.getTime()
        : (liveColumnarStore.latestTs(selected) ?? Date.now());
      const startMs = endMs - windowMin * 60 * 1000;
      const sources = sourcesStore.sources();
      const groups: SourceGroup[] = [];
      for (const id of selected) {
        // Resolve by NAME to the server's streamed source id (arrow source_registry). Skip sources not being
        // streamed this session so the timeline never plots another boat's data via a source-id collision.
        const info = sources.find((s) => Number(s.source_id) === Number(id));
        const name = info?.source_name;
        const frameId = name ? liveColumnarStore.sourceId(name) : undefined;
        if (frameId === undefined) continue;
        const rows = liveColumnarStore.windowRows(frameId, startMs, endMs);
        if (!rows.length) continue;
        const color = info?.color || sourcesStore.getSourceColor(name) || "#1f77b4";
        groups.push({ sourceId: id, sourceName: name, color, data: rows as LiveDataPoint[] });
      }
      return groups;
    }

    const newDataMap = streamingStore.getNewData()();
    let totalNewDataPoints = 0;
    let latestNewTimestamp = 0;
    for (const [sourceId, points] of newDataMap.entries()) {
      if (selected.has(sourceId) && points?.length) {
        totalNewDataPoints += points.length;
        for (const point of points) {
          const ts = getTimestamp(point).getTime();
          if (ts > latestNewTimestamp) latestNewTimestamp = ts;
        }
      }
    }
    void totalNewDataPoints;
    void latestNewTimestamp;

    const filterOpts = isReplayMode()
      ? liveReplayFilteredDataOptions(validDate(referenceTime) ? referenceTime : null)
      : {
          effectivePlaybackTime: validDate(referenceTime) ? referenceTime : null,
        };

    const filteredDataMap = streamingStore.getFilteredData(selected, filterOpts);

    let totalDataCount = 0;
    let latestTimestamp = 0;
    for (const data of filteredDataMap.values()) {
      totalDataCount += data.length;
      if (data.length > 0) {
        const ts = getTimestamp(data[data.length - 1]).getTime();
        if (ts > latestTimestamp) latestTimestamp = ts;
      }
    }
    void totalDataCount;
    void latestTimestamp;

    void streamingStore.getHistoricalDataVersion()();
    void streamingStore.getLiveDataAppendVersion()();
    if (currentlyPlaying) {
      void smoothPlaybackTimeForTrack();
    }

    const sources = sourcesStore.sources();
    const groups: SourceGroup[] = [];

    for (const [sourceId, data] of filteredDataMap.entries()) {
      if (data.length === 0) continue;

      const sourceInfo = sources.find((s) => Number(s.source_id) === Number(sourceId));
      const color = sourceInfo?.color || "#1f77b4";
      const name = sourceInfo?.source_name || String(sourceId);
      let sorted = data as LiveDataPoint[];

      if (currentTimeWindow === 0 && currentlyPlaying && validDate(playhead)) {
        const currentTimeMs = playhead.getTime();
        sorted = sorted.filter((d) => getTimestamp(d).getTime() <= currentTimeMs);
      }

      groups.push({
        sourceId: Number(sourceId),
        sourceName: name,
        color,
        data: sorted,
      });
    }

    return groups;
  });

  const timeExtent = createMemo<[Date, Date]>(() => {
    const currentTimeWindow = Number(timeWindow());

    // THIS is the branch that sets the x scale on a live map (the window dial is never 0 there), so
    // it is where a scrub used to drag the frame: it read the playhead directly, so clicking
    // re-based the window and the clicked instant became the right-hand edge. Anchored, the frame
    // holds still and only the playhead marker moves inside it.
    if (currentTimeWindow > 0) {
      const endMs = windowAnchorMs();
      const windowMs = currentTimeWindow * 60 * 1000;
      return [new Date(endMs - windowMs), new Date(endMs)];
    }

    const groups = groupedData();
    if (groups.length === 0) {
      const now = new Date();
      return [new Date(now.getTime() - DEFAULT_WINDOW_MS), now];
    }

    let minTime = Infinity;
    let maxTime = -Infinity;
    for (const group of groups) {
      for (const point of group.data) {
        const ts = getTimestamp(point).getTime();
        if (ts > 0) {
          minTime = Math.min(minTime, ts);
          maxTime = Math.max(maxTime, ts);
        }
      }
    }

    if (minTime === Infinity || maxTime === -Infinity || minTime === maxTime) {
      const now = new Date();
      return [new Date(now.getTime() - DEFAULT_WINDOW_MS), now];
    }

    return [new Date(minTime), new Date(maxTime)];
  });

  const valueExtent = createMemo<[number, number]>(() => {
    const groups = groupedData();
    if (groups.length === 0) return FALLBACK_Y_RANGE;

    const bspFieldName = defaultChannelsStore.bspName() || "Bsp_kph";
    let minVal = Infinity;
    let maxVal = -Infinity;

    for (const group of groups) {
      for (const d of group.data) {
        const val = getBspValue(d, bspFieldName);
        if (val !== undefined) {
          minVal = Math.min(minVal, val);
          maxVal = Math.max(maxVal, val);
        }
      }
    }

    if (!Number.isFinite(minVal) || !Number.isFinite(maxVal) || minVal === Infinity) {
      return FALLBACK_Y_RANGE;
    }

    if (minVal === maxVal) {
      const pad = Math.max(1, Math.abs(minVal) * 0.05);
      return [minVal - pad, maxVal + pad];
    }

    return [minVal * 0.95, maxVal * 1.05];
  });

  const buildChartModel = (): ChartModel => {
    const groups = groupedData();
    const [minTime, maxTime] = timeExtent();
    const [minVal, maxVal] = valueExtent();
    const xMinSec = minTime.getTime() / 1000;
    const xMaxSec = maxTime.getTime() / 1000;
    const bspFieldName = defaultChannelsStore.bspName() || "Bsp_kph";
    const timesMsSet = new Set<number>();
    const yBySource = new Map<number, Map<number, number>>();
    const seriesMeta: SeriesMeta[] = [];

    for (const group of groups) {
      const values = new Map<number, number>();
      for (const point of group.data) {
        const ts = getTimestamp(point).getTime();
        if (ts < minTime.getTime() || ts > maxTime.getTime()) continue;
        const y = getBspValue(point, bspFieldName);
        if (y === undefined) continue;
        values.set(ts, y);
        timesMsSet.add(ts);
      }
      seriesMeta.push({
        sourceId: group.sourceId,
        label: group.sourceName,
        color: group.color,
      });
      yBySource.set(group.sourceId, values);
    }

    const timesMs = [...timesMsSet].sort((a, b) => a - b);
    const xValues = timesMs.length > 0 ? timesMs.map((t) => t / 1000) : [xMinSec, xMaxSec];
    const yColumns = seriesMeta.map((meta) => {
      const values = yBySource.get(meta.sourceId);
      if (!values || timesMs.length === 0) return xValues.map(() => null);
      return timesMs.map((t) => values.get(t) ?? null);
    });

    return {
      data: [xValues, ...yColumns] as uPlot.AlignedData,
      seriesMeta,
      xRange: xMaxSec > xMinSec ? [xMinSec, xMaxSec] : [xMinSec, xMinSec + 60],
      yRange: maxVal > minVal ? [minVal, maxVal] : FALLBACK_Y_RANGE,
    };
  };

  const drawPlayhead = (u: uPlot) => {
    const refTime = getDisplayWindowReferenceTime();
    const currentTime = validDate(refTime) ? refTime : selectedTime();
    if (!validDate(currentTime)) return;

    const min = u.scales.x.min;
    const max = u.scales.x.max;
    if (min == null || max == null || max <= min) return;

    const tSec = currentTime.getTime() / 1000;
    if (tSec < min - 60 || tSec > max + 60) return;

    const { left, top, width, height } = u.bbox;
    const x = left + ((tSec - min) / (max - min)) * width;
    if (!Number.isFinite(x)) return;

    // Paused/scrubbed reads differently from live. Without this the playhead looks identical whether
    // it is chasing the feed or parked where the user put it, which is the one thing the user needs
    // to be able to tell at a glance on a page that is otherwise always moving.
    const scrubbed = scrubEnabled() && !liveFollow();

    const ctx = u.ctx;
    ctx.save();
    ctx.strokeStyle = scrubbed ? "#38bdf8" : "#e11";
    ctx.lineWidth = 2;
    if (scrubbed) ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(x, top - 15);
    ctx.lineTo(x, top + height - 15);
    ctx.stroke();
    ctx.restore();
  };

  const drawAxisFrame = (u: uPlot) => {
    const axisColor = themeStore.isDark() ? "#cbd5e1" : "#374151";
    const { left, top, width, height } = u.bbox;
    const ctx = u.ctx;
    ctx.save();
    ctx.strokeStyle = axisColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(left, top);
    ctx.lineTo(left, top + height);
    ctx.moveTo(left, top + height);
    ctx.lineTo(left + width, top + height);
    ctx.stroke();
    ctx.restore();
  };

  /**
   * Pointer-driven scrubbing over the live timeline.
   *
   * The timeline used to be inert by design (every uPlot pointer binding returned null), so the only
   * way to move the live playhead was the PlayPause time-picker dialog. Click-to-seek and
   * drag-the-playhead are what make "pause and look back" reachable.
   *
   * Seeks go through `seekLive`, never `setSelectedTime` directly: a live seek has to drop
   * `liveFollow` too, or the live tick's auto-resume restores the clock ~2s later and the map snaps
   * back to the data edge.
   *
   * The drag is rAF-coalesced. A pointermove burst can arrive many times per frame, and each seek
   * dispatches a `playbackSelectedTimeChange` event and re-windows every layer, so seeking per event
   * would do that work several times for a single painted frame.
   */
  const attachScrubHandlers = (u: uPlot) => {
    if (!scrubEnabled()) return;
    const over = u.over;
    if (!over) return;

    over.style.cursor = "col-resize";

    let scrubbing = false;
    let pendingMs: number | null = null;
    let scrubRaf: number | null = null;

    /** Pointer x (client px) -> epoch ms, clamped to the drawn window. */
    const msAt = (clientX: number): number | null => {
      const rect = over.getBoundingClientRect();
      const left = clientX - rect.left;
      const sec = u.posToVal(left, "x");
      if (!Number.isFinite(sec)) return null;
      const min = u.scales.x.min;
      const max = u.scales.x.max;
      const clamped = min != null && max != null ? Math.min(Math.max(sec, min), max) : sec;
      return clamped * 1000;
    };

    const flush = () => {
      scrubRaf = null;
      if (pendingMs == null) return;
      const ms = pendingMs;
      pendingMs = null;
      seekLive(ms, "livemaptimeseries");
    };

    const queueSeek = (ms: number) => {
      pendingMs = ms;
      if (scrubRaf == null) scrubRaf = requestAnimationFrame(flush);
    };

    const onPointerDown = (ev: PointerEvent) => {
      if (ev.button !== 0) return;
      const ms = msAt(ev.clientX);
      if (ms == null) return;
      scrubbing = true;
      // Pointer capture keeps the drag alive when the cursor leaves the plot, so releasing outside
      // the chart still ends the scrub instead of leaving it latched.
      try { over.setPointerCapture(ev.pointerId); } catch { /* not all pointers capture */ }
      ev.preventDefault();
      queueSeek(ms);
    };

    const onPointerMove = (ev: PointerEvent) => {
      if (!scrubbing) return;
      const ms = msAt(ev.clientX);
      if (ms != null) queueSeek(ms);
    };

    const endScrub = (ev: PointerEvent) => {
      if (!scrubbing) return;
      scrubbing = false;
      try { over.releasePointerCapture(ev.pointerId); } catch { /* already released */ }
      // Land the final position immediately rather than waiting on the queued frame, so where the
      // user let go is exactly where the playhead ends up.
      const ms = msAt(ev.clientX);
      if (ms != null) pendingMs = ms;
      if (scrubRaf != null) {
        cancelAnimationFrame(scrubRaf);
        scrubRaf = null;
      }
      flush();
    };

    over.addEventListener("pointerdown", onPointerDown);
    over.addEventListener("pointermove", onPointerMove);
    over.addEventListener("pointerup", endScrub);
    over.addEventListener("pointercancel", endScrub);

    scrubTeardown = () => {
      if (scrubRaf != null) {
        cancelAnimationFrame(scrubRaf);
        scrubRaf = null;
      }
      over.removeEventListener("pointerdown", onPointerDown);
      over.removeEventListener("pointermove", onPointerMove);
      over.removeEventListener("pointerup", endScrub);
      over.removeEventListener("pointercancel", endScrub);
    };
  };

  const createOptions = (model: ChartModel, width: number, height: number): uPlot.Options => {
    const axisColor = themeStore.isDark() ? "#cbd5e1" : "#374151";
    const timezone = getTimezone();

    return {
      width,
      height,
      padding: CHART_PADDING,
      series: [
        { label: "Time" },
        ...model.seriesMeta.map((meta) => ({
          label: meta.label,
          stroke: meta.color,
          scale: "y",
          width: 1,
          spanGaps: (LINE_GAP_THRESHOLD_MS / 1000) as unknown as boolean,
          points: { show: false },
          value: (_u: uPlot, raw: number | null) => (raw == null ? "—" : String(Number(raw).toFixed(2))),
        })),
      ],
      scales: {
        x: {
          time: true,
          range: () => currentModel?.xRange ?? model.xRange,
        },
        y: {
          range: () => currentModel?.yRange ?? model.yRange,
        },
      },
      axes: [
        {
          stroke: axisColor,
          font: "12px sans-serif",
          grid: { show: false },
          ticks: {
            show: true,
            stroke: axisColor,
            width: 1,
            size: X_TICK_SIZE_PX,
          },
          incrs: [1, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600],
          values: (_u, vals) => vals.map((v) => formatTime(new Date(Number(v) * 1000), timezone) ?? ""),
          space: 80,
          side: 2,
          size: 30,
        },
        {
          stroke: axisColor,
          font: "12px sans-serif",
          grid: { show: false },
          ticks: {
            show: true,
            stroke: axisColor,
            width: 1,
            size: 4,
          },
          splits: (_u, _idx, min, max) => yAxisSplits(min, max),
          values: (_u, splits) => splits.map((v) => formatYAxisTick(v)),
          space: 45,
          side: 3,
          size: Y_AXIS_GUTTER_PX,
        },
      ],
      cursor: {
        // uPlot's own cursor stays off: this timeline shows a playhead, not a hover readout, and its
        // built-in drag/zoom would fight the scrub. The pointer handlers are attached to `u.over` in
        // the `ready` hook below, which keeps them out of uPlot's cursor state machine entirely.
        lock: true,
        x: false,
        y: false,
        points: { show: false },
        bind: {
          mousedown: () => null,
          mouseup: () => null,
          click: () => null,
          dblclick: () => null,
          mousemove: () => null,
        },
      },
      select: { show: false, left: 0, top: 0, width: 0, height: 0 },
      legend: { show: false },
      hooks: {
        draw: [drawAxisFrame, drawPlayhead],
        ready: [attachScrubHandlers],
      },
    };
  };

  const destroyPlot = () => {
    if (scrubTeardown) {
      // Before destroy(): the listeners live on `u.over`, which destroy() removes from the DOM.
      scrubTeardown();
      scrubTeardown = null;
    }
    if (!plot) return;
    try {
      plot.destroy();
    } catch {
      /* noop */
    }
    plot = null;
    lastSeriesSignature = "";
  };

  const updatePlot = () => {
    if (!chartContainer || !isMounted || abortController?.signal.aborted) return;

    const bbox = chartContainer.getBoundingClientRect();
    const width = Math.max(Math.round(bbox.width), 100);
    const height = Math.max(Math.round(bbox.height), 80);
    const model = buildChartModel();
    const seriesSignature = model.seriesMeta
      .map((meta) => `${meta.sourceId}:${meta.label}:${meta.color}`)
      .join("|") + `|theme:${themeStore.isDark() ? "dark" : "light"}`;

    currentModel = model;

    if (!plot || lastSeriesSignature !== seriesSignature) {
      destroyPlot();
      try {
        plot = new uPlot(createOptions(model, width, height), model.data, chartContainer);
        lastSeriesSignature = seriesSignature;
      } catch (err) {
        warnThrottled("uplot-create", "[LiveMapUPlotTimeSeries] uPlot create failed", err);
        return;
      }
    } else {
      try {
        plot.setSize({ width, height });
        plot.setData(model.data);
      } catch (err) {
        warnThrottled("uplot-update", "[LiveMapUPlotTimeSeries] uPlot update failed", err);
        return;
      }
    }

    try {
      plot.setScale("x", { min: model.xRange[0], max: model.xRange[1] });
      plot.setScale("y", { min: model.yRange[0], max: model.yRange[1] });
      plot.redraw();
    } catch {
      /* ignore if chart was destroyed during a resize/update race */
    }
  };

  const scheduleRender = () => {
    if (renderRafId != null) return;
    renderRafId = requestAnimationFrame(() => {
      renderRafId = null;
      updatePlot();
    });
  };

  const loadInitialData = async () => {
    if (!isMounted || abortController?.signal.aborted) {
      logDebug("[LiveMapUPlotTimeSeries] Component unmounted or aborted, skipping initial load");
      return;
    }

    const selected = selectedSourceIds();
    if (selected.size === 0) return;

    try {
      const currentTimeWindow = Number(timeWindow());
      const endTime = Date.now();
      const minutes = currentTimeWindow > 0 ? currentTimeWindow : 0;

      logDebug("[LiveMapUPlotTimeSeries] Loading initial history", {
        timeWindow: currentTimeWindow,
        minutes,
        endTime: new Date(endTime).toISOString(),
        sourceCount: selected.size,
      });

      await streamingStore.loadInitialDataFromRedis(selected, minutes, endTime);

      if (!isMounted || abortController?.signal.aborted) {
        logDebug("[LiveMapUPlotTimeSeries] Component unmounted or aborted after initial load");
        return;
      }

      const rawDataMap = streamingStore.getRawData(selected);
      const allData: LiveDataPoint[] = [];
      for (const data of rawDataMap.values()) {
        allData.push(...(data as LiveDataPoint[]));
      }
      allData.sort((a, b) => getTimestamp(a).getTime() - getTimestamp(b).getTime());

      if (allData.length > 0 && !hasInitializedSelectedTime) {
        const latestTime = Math.max(...allData.map((d) => getTimestamp(d).getTime()));
        const earliestTime = Math.min(...allData.map((d) => getTimestamp(d).getTime()));
        if (latestTime > 0) {
          const currentTime = selectedTime();
          const defaultTime = new Date("1970-01-01T12:00:00Z");
          const isDefault = currentTime.getTime() === defaultTime.getTime();
          const bufferMsVal = liveConfigStore.bufferMs();
          const initialTime = Math.max(earliestTime, latestTime - bufferMsVal);

          if (isDefault || Math.abs(currentTime.getTime() - latestTime) > 60_000) {
            setIsManualTimeChange(false);
            setSelectedTime(new Date(initialTime), "playback", true);
          }
          hasInitializedSelectedTime = true;
        }
      }

      if (props.onMapUpdate && allData.length > 0 && isMounted && !abortController?.signal.aborted) {
        props.onMapUpdate(allData);
      }

      if (allData.length > 0 && isMounted && !abortController?.signal.aborted && !isPlaying()) {
        setIsManualTimeChange(false);
        setIsPlaying(true);
        logDebug("[LiveMapUPlotTimeSeries] Auto-started playback after the initial history load");
      }
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === "AbortError" || abortController?.signal.aborted) {
        logDebug("[LiveMapUPlotTimeSeries] Initial load aborted");
        return;
      }
      logWarn("[LiveMapUPlotTimeSeries] Error loading initial data:", err);
    }
  };

  createEffect(() => {
    if (!isMounted || abortController?.signal.aborted) return;

    const selected = selectedSourceIds();
    if (!selected || selected.size === 0) return;

    if (sameSourceSet(selected, lastLoadedSourceIds)) return;

    lastLoadedSourceIds = new Set(selected);
    logDebug("[LiveMapUPlotTimeSeries] selectedSourceIds available, loading history", {
      sourceIds: [...selected].sort((a, b) => a - b),
    });
    loadInitialData().catch((err: unknown) => {
      if ((err as { name?: string })?.name === "AbortError" || abortController?.signal.aborted) {
        logDebug("[LiveMapUPlotTimeSeries] loadInitialData aborted");
        return;
      }
      logWarn("[LiveMapUPlotTimeSeries] Error loading initial data (sources changed):", err);
    });
  });

  onMount(() => {
    abortController = new AbortController();
    isMounted = true;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        if (!chartContainer || !isMounted) return;
        const { width, height } = chartContainer.getBoundingClientRect();
        if (width > 0 && height > 0) {
          scheduleRender();
        }
      });
      queueMicrotask(() => {
        if (chartContainer) resizeObserver?.observe(chartContainer);
      });
    }

    loadInitialData().catch((err: unknown) => {
      if ((err as { name?: string })?.name === "AbortError" || abortController?.signal.aborted) {
        logDebug("[LiveMapUPlotTimeSeries] Initial load aborted");
        return;
      }
      logWarn("[LiveMapUPlotTimeSeries] Initial load failed", err);
    });
  });

  createEffect(() => {
    if (!isMounted || abortController?.signal.aborted) return;

    const currentTimeWindow = Number(timeWindow());
    const selected = selectedSourceIds();

    if (selected.size === 0 || currentTimeWindow === lastTimeWindow || isFetchingData) {
      lastTimeWindow = currentTimeWindow;
      return;
    }

    isFetchingData = true;
    lastTimeWindow = currentTimeWindow;
    const replayLive = isReplayMode();
    const wasPlaying = isPlaying();

    (async () => {
      try {
        if (!isMounted || abortController?.signal.aborted) return;

        // Machinery, not a gesture — see stopPlaybackClock in playbackStore.
        if (!replayLive && wasPlaying) {
          stopPlaybackClock();
          logDebug("[LiveMapUPlotTimeSeries] Paused playback for timeWindow change");
        }

        const endTime = Date.now();
        const minutes = currentTimeWindow > 0 ? currentTimeWindow : 0;

        logDebug("[LiveMapUPlotTimeSeries] TimeWindow changed, fetching history", {
          timeWindow: currentTimeWindow,
          minutes,
          endTime: new Date(endTime).toISOString(),
          sourceCount: selected.size,
        });

        await streamingStore.loadInitialDataFromRedis(selected, minutes, endTime);

        if (!isMounted || abortController?.signal.aborted) {
          logDebug("[LiveMapUPlotTimeSeries] Component unmounted or aborted after timeWindow fetch");
          return;
        }

        logDebug("[LiveMapUPlotTimeSeries] Data fetched for timeWindow");
      } catch (err: unknown) {
        if ((err as { name?: string })?.name === "AbortError" || abortController?.signal.aborted) {
          logDebug("[LiveMapUPlotTimeSeries] timeWindow fetch aborted");
          return;
        }
        logWarn("[LiveMapUPlotTimeSeries] Error fetching data for timeWindow change:", err);
      } finally {
        if (isMounted && !abortController?.signal.aborted && !replayLive && wasPlaying) {
          setTimeout(() => {
            if (isMounted && !abortController?.signal.aborted) {
              setIsPlaying(true);
              logDebug("[LiveMapUPlotTimeSeries] Resumed playback after timeWindow data fetch");
            }
          }, 100);
        }
        isFetchingData = false;
      }
    })();
  });

  createEffect(() => {
    if (!isMounted || abortController?.signal.aborted) return;
    void groupedData();
    void timeExtent();
    void valueExtent();
    void themeStore.isDark();
    void defaultChannelsStore.bspName();
    void selectedTime();
    // Follow state changes the playhead's own styling (solid red live, dashed blue scrubbed), and
    // nothing else in this effect moves when the user merely pauses in place.
    void liveFollow();
    if (isPlaying()) {
      void smoothPlaybackTimeForTrack();
    }
    void streamingStore.getHistoricalDataVersion()();
    void streamingStore.getLiveDataAppendVersion()();
    scheduleRender();
  });

  createEffect(() => {
    if (props.incrementalMapUpdates === false) return;
    if (!isMounted || abortController?.signal.aborted) return;

    const streamEpoch = streamingStore.getStreamingLifecycleEpoch()();
    if (streamEpoch !== lastSeenStreamLifecycle) {
      lastSeenStreamLifecycle = streamEpoch;
      lastMapUpdateSignature = null;
    }

    const selected = selectedSourceIds();
    if (!selected || selected.size === 0) {
      props.onMapUpdate?.([]);
      return;
    }

    const currentlyPlaying = isPlaying();
    if (currentlyPlaying && !prevIsPlayingForMap) {
      lastMapUpdateSignature = null;
    }
    prevIsPlayingForMap = currentlyPlaying;

    if (!currentlyPlaying) return;

    const newDataMap = streamingStore.getNewData()();
    const liveAppendVersion = streamingStore.getLiveDataAppendVersion()();
    const historicalDataVersion = streamingStore.getHistoricalDataVersion()();

    const rawDataMap = streamingStore.getRawData(selected);

    // Signature check first: it depends only on rawDataMap and the version counters, never on
    // allData. Building and sorting allData (and scanning every new point for the diagnostic)
    // before this early return meant an O(n log n) sort on every no-op tick — and this effect
    // re-runs on each WebSocket append.
    const sortedSelected = [...selected].sort((a, b) => a - b);
    let mapSignature = `${historicalDataVersion}|${liveAppendVersion}|${sourceSetKey(selected)}`;
    for (const sid of sortedSelected) {
      const data = rawDataMap.get(sid);
      const len = data?.length ?? 0;
      const maxTs = len > 0 && data ? getTimestamp(data[len - 1]).getTime() : 0;
      mapSignature += `|${sid}:${len}:${maxTs}`;
    }
    if (mapSignature === lastMapUpdateSignature) return;
    lastMapUpdateSignature = mapSignature;

    const allData: LiveDataPoint[] = [];
    for (const data of rawDataMap.values()) {
      allData.push(...(data as LiveDataPoint[]));
    }
    allData.sort((a, b) => getTimestamp(a).getTime() - getTimestamp(b).getTime());

    let newDataCount = 0;
    let latestNewTimestamp = 0;
    for (const [sourceId, points] of newDataMap.entries()) {
      if (selected.has(sourceId) && points?.length) {
        newDataCount += points.length;
        for (const point of points) {
          const ts = getTimestamp(point).getTime();
          if (ts > latestNewTimestamp) latestNewTimestamp = ts;
        }
      }
    }

    logDebug("[LiveMapUPlotTimeSeries] WebSocket data effect triggered (map update only)", {
      newDataCount,
      latestNewTimestamp,
      allDataLength: allData.length,
      isPlaying: currentlyPlaying,
    });

    props.onMapUpdate?.(allData);
  });

  createEffect(() => {
    if (!isMounted || abortController?.signal.aborted) return;

    const currentTime = selectedTime();
    const currentlyPlaying = isPlaying();
    const selected = selectedSourceIds();
    if (!currentlyPlaying || selected.size === 0) return;

    const latestTimestamp = streamingStore.getLatestTimestamp(selected);
    if (!latestTimestamp) return;

    const currentTimeMs = validDate(currentTime) ? currentTime.getTime() : 0;
    if (currentTimeMs === 0) return;

    const timeDiff = latestTimestamp - currentTimeMs;
    if (timeDiff > END_THRESHOLD_MS || timeDiff < -END_THRESHOLD_MS) return;

    // Deferred while the user is waiting on a page load — this is another channels x sources wave
    // competing with an in-flight import. `lastEndOfDataFetchMs` is deliberately NOT stamped on the
    // yielded path, so the next run after the window closes fetches immediately rather than waiting
    // out a fresh interval. See utils/renderYield.ts.
    const nowMs = Date.now();
    const minInterval = Math.max(endOfDataBackoff.delayMs(), liveConfigStore.pollIntervalMs());
    if (endOfDataFetchInFlight || isRenderYieldActive() || nowMs - lastEndOfDataFetchMs < minInterval) {
      return;
    }
    lastEndOfDataFetchMs = nowMs;
    // Sitting at the edge is the stalled case by construction, so let a feed that never advances
    // decay to LIVE_POLL_MAX_MS instead of re-asking every second forever.
    endOfDataBackoff.observeEdge(latestTimestamp);

    logDebug("[LiveMapUPlotTimeSeries] Reached end of available data, fetching missing data", {
      currentTime: currentTimeMs,
      latestTimestamp,
      timeDiff,
    });

    const fetchMissingData = async () => {
      if (!isMounted || abortController?.signal.aborted) return;

      endOfDataFetchInFlight = true;
      try {
        const windowMin = Number(timeWindow());
        const minutes = windowMin > 0 ? windowMin : 0;
        await streamingStore.loadInitialDataFromRedis(selected, minutes, Date.now());

        if (!isMounted || abortController?.signal.aborted) return;

        const newLatestTimestamp = streamingStore.getLatestTimestamp(selected);
        if (newLatestTimestamp && newLatestTimestamp > latestTimestamp) {
          logDebug("[LiveMapUPlotTimeSeries] Fetched new data, latest timestamp now:", newLatestTimestamp);
        } else {
          logDebug("[LiveMapUPlotTimeSeries] No newer history available");
        }
      } catch (err: unknown) {
        if ((err as { name?: string })?.name === "AbortError" || abortController?.signal.aborted) {
          logDebug("[LiveMapUPlotTimeSeries] fetchMissingData aborted");
          return;
        }
        logWarn("[LiveMapUPlotTimeSeries] Error fetching missing data:", err);
      } finally {
        // finally, not the end of try: the two early returns above (unmounted / aborted) would
        // otherwise latch this true forever and no catch-up would ever run again.
        endOfDataFetchInFlight = false;
      }
    };

    void fetchMissingData();
  });

  onCleanup(() => {
    logDebug("[LiveMapUPlotTimeSeries] onCleanup called - aborting operations");
    isMounted = false;

    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    if (renderRafId != null) {
      cancelAnimationFrame(renderRafId);
      renderRafId = null;
    }

    resizeObserver?.disconnect();
    resizeObserver = null;
    destroyPlot();
    currentModel = null;
    logDebug("[LiveMapUPlotTimeSeries] Cleanup complete");
  });

  return <div ref={(el) => (chartContainer = el)} class="chart-container live-map-uplot-timeseries" />;
}
